import { format, parseISO } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

/**
 * Formats a date/time string in the user's timezone
 * @param dateString - ISO date string to format
 * @param userTimezone - User's timezone (e.g., 'America/New_York')
 * @param formatStr - date-fns format string (defaults to 'PPp' for localized date and time)
 * @returns Formatted date string in user's timezone
 */
export const formatInUserTimezone = (
  dateString: string,
  userTimezone: string,
  formatStr: string = 'PPp'
): string => {
  if (!dateString || !userTimezone) return dateString;
  
  try {
    // Ensure the dateString is treated as UTC if it doesn't have timezone info
    let isoString = dateString;
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      isoString = dateString + 'Z';
    }
    
    const utcDate = parseISO(isoString);
    const zonedDate = toZonedTime(utcDate, userTimezone);
    return format(zonedDate, formatStr);
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    return dateString;
  }
};

/**
 * Converts a date from user's timezone to UTC for storage
 * @param date - Date object in user's timezone
 * @param userTimezone - User's timezone
 * @returns UTC date
 */
export const convertToUTC = (date: Date, userTimezone: string): Date => {
  if (!userTimezone) return date;
  
  try {
    return fromZonedTime(date, userTimezone);
  } catch (error) {
    console.error('Error converting to UTC:', error);
    return date;
  }
};

/**
 * Helper function to create day bounds using browser timezone as fallback
 * @param date - Date to get bounds for
 * @returns Object with start and end of day in UTC
 */
const createFallbackDayBounds = (date: Date) => {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return {
    start: startOfDay.toISOString(),
    end: endOfDay.toISOString()
  };
};

/**
 * Gets start and end of day in user's timezone, converted to UTC for Firestore queries
 * @param date - Date to get bounds for
 * @param userTimezone - User's timezone
 * @returns Object with start and end of day in UTC
 */
export const getDayBoundsInUserTimezone = (date: Date, userTimezone: string) => {
  if (!userTimezone) {
    return createFallbackDayBounds(date);
  }

  try {
    // Get the date string in YYYY-MM-DD format
    const dateStr = date.toISOString().split('T')[0];
    
    // Create start of day (00:00:00) in user's timezone
    const startOfDayInTimezone = new Date(`${dateStr}T00:00:00`);
    const startUTC = fromZonedTime(startOfDayInTimezone, userTimezone);

    // Create end of day (23:59:59.999) in user's timezone  
    const endOfDayInTimezone = new Date(`${dateStr}T23:59:59.999`);
    const endUTC = fromZonedTime(endOfDayInTimezone, userTimezone);

    return {
      start: startUTC.toISOString(),
      end: endUTC.toISOString()
    };
  } catch (error) {
    console.error('Error getting day bounds in timezone:', error);
    return createFallbackDayBounds(date);
  }
};

/**
 * Gets the current timezone of the browser
 * @returns Browser's timezone
 */
export const getBrowserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};